import { useState, useEffect } from "react";
import { Save, ExternalLink, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const SocialTab = () => {
  const [settings, setSettings] = useState({
    twitter_handle: "",
    twitter_embed_url: "",
    facebook_page_url: "",
    instagram_handle: "",
    instagram_embed_url: "",
    youtube_channel_url: "",
    tiktok_handle: "",
    is_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/social-settings`);
      setSettings(res.data || {
        twitter_handle: "",
        twitter_embed_url: "",
        facebook_page_url: "",
        instagram_handle: "",
        instagram_embed_url: "",
        youtube_channel_url: "",
        tiktok_handle: "",
        is_enabled: false
      });
    } catch (err) {
      console.error("Error fetching social settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/social-settings`, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Error saving settings:", err);
      alert("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Social Media Settings</h2>
          <p className="text-zinc-500 text-sm mt-1">Configure your social media feeds for the homepage</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 text-green-500 text-sm">
            <Check className="w-4 h-4" /> Saved!
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Enable/Disable */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold">Enable Social Feed</h3>
              <p className="text-zinc-500 text-sm mt-1">Show social media section on the homepage</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.is_enabled}
                onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
            </label>
          </div>
        </div>

        {/* Twitter/X */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            <h3 className="text-white font-bold">X / Twitter</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Handle</label>
              <input
                type="text"
                value={settings.twitter_handle || ""}
                onChange={(e) => setSettings({ ...settings, twitter_handle: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                placeholder="@yourhandle"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Embed URL (optional)
                <a 
                  href="https://publish.twitter.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ml-2 text-orange-500 hover:text-orange-400"
                >
                  <ExternalLink className="w-3 h-3 inline" />
                </a>
              </label>
              <input
                type="url"
                value={settings.twitter_embed_url || ""}
                onChange={(e) => setSettings({ ...settings, twitter_embed_url: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                placeholder="https://platform.twitter.com/..."
              />
            </div>
          </div>
        </div>

        {/* Facebook */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <h3 className="text-white font-bold">Facebook</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Page URL</label>
            <input
              type="url"
              value={settings.facebook_page_url || ""}
              onChange={(e) => setSettings({ ...settings, facebook_page_url: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
              placeholder="https://facebook.com/yourpage"
            />
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="url(#ig-gradient)">
              <defs>
                <linearGradient id="ig-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFDC80" />
                  <stop offset="50%" stopColor="#F56040" />
                  <stop offset="100%" stopColor="#833AB4" />
                </linearGradient>
              </defs>
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <h3 className="text-white font-bold">Instagram</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Handle</label>
              <input
                type="text"
                value={settings.instagram_handle || ""}
                onChange={(e) => setSettings({ ...settings, instagram_handle: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                placeholder="@yourhandle"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Embed URL (optional)</label>
              <input
                type="url"
                value={settings.instagram_embed_url || ""}
                onChange={(e) => setSettings({ ...settings, instagram_embed_url: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* YouTube */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-[#FF0000]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <h3 className="text-white font-bold">YouTube</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Channel URL</label>
            <input
              type="url"
              value={settings.youtube_channel_url || ""}
              onChange={(e) => setSettings({ ...settings, youtube_channel_url: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
              placeholder="https://youtube.com/@yourchannel"
            />
          </div>
        </div>

        {/* TikTok */}
        <div className="bg-zinc-900/50 border border-white/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
            <h3 className="text-white font-bold">TikTok</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">Handle</label>
            <input
              type="text"
              value={settings.tiktok_handle || ""}
              onChange={(e) => setSettings({ ...settings, tiktok_handle: e.target.value })}
              className="w-full px-4 py-3 bg-black border border-white/10 text-white focus:border-orange-500 focus:outline-none"
              placeholder="@yourhandle"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold hover:bg-orange-400 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SocialTab;
